import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Clock,
  AlertTriangle,
  CheckCircle,
  User,
  Calendar,
  GripVertical,
  RefreshCw,
  Loader2,
  ChevronRight
} from 'lucide-react';

const prioridadColores = {
  URGENTE: 'bg-red-100 text-red-700 border-red-300',
  ALTA: 'bg-orange-100 text-orange-700 border-orange-300',
  MEDIA: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  BAJA: 'bg-green-100 text-green-700 border-green-300'
};

const estadoColores = {
  NUEVO: { bg: 'bg-purple-50', border: 'border-purple-300', header: 'bg-purple-100 text-purple-800' },
  EN_PROCESO: { bg: 'bg-blue-50', border: 'border-blue-300', header: 'bg-blue-100 text-blue-800' },
  EN_REVISION: { bg: 'bg-amber-50', border: 'border-amber-300', header: 'bg-amber-100 text-amber-800' },
  COMPLETADO: { bg: 'bg-green-50', border: 'border-green-300', header: 'bg-green-100 text-green-800' }
};

const TramitesKanban = ({ onTramiteClick, filtros = {} }) => {
  const [columnas, setColumnas] = useState({
    NUEVO: [],
    EN_PROCESO: [],
    EN_REVISION: [],
    COMPLETADO: []
  });
  const [loading, setLoading] = useState(true);
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const loadKanban = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtros.responsable_id) params.append('responsable_id', filtros.responsable_id);
      if (filtros.origen) params.append('origen', filtros.origen);
      if (filtros.prioridad) params.append('prioridad', filtros.prioridad);
      
      const res = await api.get(`/tramites/kanban?${params.toString()}`);
      setColumnas(res.data);
    } catch (error) {
      console.error('Error loading kanban:', error);
      toast.error('Error al cargar vista Kanban');
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => {
    loadKanban();
  }, [loadKanban]);

  const handleDragStart = (e, tramite, estadoOrigen) => {
    setDragging({ tramite, estadoOrigen });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, estado) => {
    e.preventDefault();
    setDragOver(estado);
  };

  const handleDragLeave = () => {
    setDragOver(null);
  };

  const handleDrop = async (e, estadoDestino) => {
    e.preventDefault();
    setDragOver(null);
    
    if (!dragging || dragging.estadoOrigen === estadoDestino) {
      setDragging(null);
      return;
    }

    const { tramite, estadoOrigen } = dragging;
    
    // Actualización optimista
    setColumnas(prev => {
      const nuevasColumnas = { ...prev };
      nuevasColumnas[estadoOrigen] = prev[estadoOrigen].filter(t => t.id !== tramite.id);
      nuevasColumnas[estadoDestino] = [...prev[estadoDestino], { ...tramite, estado: estadoDestino }];
      return nuevasColumnas;
    });

    try {
      await api.put(`/tramites/kanban/${tramite.id}/mover?nuevo_estado=${estadoDestino}`);
      toast.success(`Trámite movido a ${estadoDestino.replace('_', ' ')}`);
    } catch (error) {
      console.error('Error moving tramite:', error);
      toast.error('Error al mover trámite');
      loadKanban(); // Recargar para revertir
    }
    
    setDragging(null);
  };

  const isVencido = (fecha) => {
    if (!fecha) return false;
    return new Date(fecha) < new Date();
  };

  const diasRestantes = (fecha) => {
    if (!fecha) return null;
    const hoy = new Date();
    const venc = new Date(fecha);
    const diff = Math.ceil((venc - hoy) / (1000 * 60 * 60 * 24));
    return diff;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-indigo-600" size={32} />
      </div>
    );
  }

  return (
    <div className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: '600px' }}>
      {Object.entries(columnas).map(([estado, tramites]) => {
        const colores = estadoColores[estado];
        const isDragTarget = dragOver === estado;
        
        return (
          <div
            key={estado}
            className={`flex-shrink-0 w-80 rounded-lg border-2 ${colores.border} ${colores.bg} ${
              isDragTarget ? 'ring-2 ring-indigo-400 ring-offset-2' : ''
            }`}
            onDragOver={(e) => handleDragOver(e, estado)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, estado)}
          >
            {/* Header de columna */}
            <div className={`p-3 ${colores.header} rounded-t-md flex items-center justify-between`}>
              <div className="flex items-center gap-2">
                {estado === 'NUEVO' && <Clock size={18} />}
                {estado === 'EN_PROCESO' && <RefreshCw size={18} />}
                {estado === 'EN_REVISION' && <AlertTriangle size={18} />}
                {estado === 'COMPLETADO' && <CheckCircle size={18} />}
                <span className="font-semibold">{estado.replace('_', ' ')}</span>
              </div>
              <Badge variant="secondary" className="bg-white/50">
                {tramites.length}
              </Badge>
            </div>

            {/* Lista de trámites */}
            <div className="p-2 space-y-2 min-h-[500px]">
              {tramites.map((tramite) => {
                const dias = diasRestantes(tramite.fecha_vencimiento);
                const vencido = isVencido(tramite.fecha_vencimiento) && estado !== 'COMPLETADO';
                
                return (
                  <Card
                    key={tramite.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, tramite, estado)}
                    className={`p-3 bg-white cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow ${
                      vencido ? 'border-red-400 border-2' : ''
                    } ${dragging?.tramite.id === tramite.id ? 'opacity-50' : ''}`}
                    onClick={() => onTramiteClick?.(tramite.id)}
                  >
                    <div className="flex items-start gap-2">
                      <GripVertical size={16} className="text-slate-400 mt-1 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-slate-900 truncate">
                          {tramite.titulo}
                        </p>
                        
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <Badge className={`text-xs ${prioridadColores[tramite.prioridad] || 'bg-slate-100'}`}>
                            {tramite.prioridad}
                          </Badge>
                          {tramite.origen && (
                            <Badge variant="outline" className="text-xs">
                              {tramite.origen}
                            </Badge>
                          )}
                        </div>

                        {/* Progreso de subtareas */}
                        {tramite.subtareas_total > 0 && (
                          <div className="mt-2">
                            <div className="flex items-center justify-between text-xs text-slate-500">
                              <span>Subtareas</span>
                              <span>{tramite.subtareas_completadas}/{tramite.subtareas_total}</span>
                            </div>
                            <div className="w-full bg-slate-200 rounded-full h-1.5 mt-1">
                              <div
                                className="bg-indigo-600 h-1.5 rounded-full"
                                style={{ width: `${(tramite.subtareas_completadas / tramite.subtareas_total) * 100}%` }}
                              />
                            </div>
                          </div>
                        )}

                        <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
                          <div className="flex items-center gap-1">
                            <User size={12} />
                            <span className="truncate max-w-[100px]">{tramite.responsable_nombre}</span>
                          </div>
                          {tramite.fecha_vencimiento && (
                            <div className={`flex items-center gap-1 ${vencido ? 'text-red-600 font-medium' : dias <= 3 ? 'text-amber-600' : ''}`}>
                              <Calendar size={12} />
                              <span>
                                {vencido ? `Vencido hace ${Math.abs(dias)}d` : 
                                 dias === 0 ? 'Hoy' :
                                 dias === 1 ? 'Mañana' :
                                 `${dias}d`}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-slate-400 flex-shrink-0" />
                    </div>
                  </Card>
                );
              })}
              
              {tramites.length === 0 && (
                <div className="text-center py-8 text-slate-400 text-sm">
                  Sin trámites
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TramitesKanban;
